/**
 * Whether `target` is an element that takes typed text: an input, a textarea
 * or a contentEditable element. A keyboard shortcut leaves a key alone while
 * such an element has focus, so the key reaches the text the user types.
 */
export function isTextEntryElement(
    target: EventTarget | null | undefined
): boolean {
    if (!(target instanceof HTMLElement)) {
        return false;
    }
    return (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable === true
    );
}

/**
 * Whether a dialog, modal or menu is open, wherever the focus is. A keyboard
 * shortcut also leaves its key alone then, so it does not act on the view
 * behind it. A modal's root counts though its content may carry no dialog
 * role; a closed modal kept mounted, hidden in the document, does not.
 */
export function isDialogOpen(): boolean {
    return Array.from(
        document.querySelectorAll(
            '.MuiModal-root, [role="dialog"], [role="alertdialog"]'
        )
    ).some((dialog) => !dialog.closest('.MuiModal-hidden'));
}

/**
 * Pure: the reason, if any, that the app must not claim this key event for a
 * keyboard shortcut, or null when it may.
 */
export function shortcutRefusal(
    event: KeyboardEvent,
    activeElement: Element | null,
    hasOpenDialog: boolean,
    isToolActionInProgress: boolean
): string | null {
    // A key inside a field belongs to the text the user types.
    if (isTextEntryElement(activeElement)) {
        return 'text-input';
    }
    // A dialog has the user's attention, and the view behind it is unseen.
    if (hasOpenDialog) {
        return 'dialog-open';
    }
    // A key must not act on a shape or a stroke still being drawn.
    if (isToolActionInProgress) {
        return 'tool-action-in-progress';
    }
    // A held key would act faster than the view redraws, so the user could
    // not see what each step did. One press, one action.
    if (event.repeat) {
        return 'auto-repeat';
    }
    return null;
}
